import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Building, Search, MapPin, Phone, Mail, Globe, CheckCircle, Navigation, Plus, MessageCircle, X, Trash2 } from "lucide-react";
import { AddProviderForm } from "./AddProviderForm";
import { config } from "@/src/config";
import { authFetch } from '@/lib/authFetch';

interface Provider {
  id?: string;
  provider_id?: string;
  organization_name?: string;
  organization_type?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  primary_phone?: string;
  primary_email?: string;
  website?: string;
  latitude?: number;
  longitude?: number;
  distance_miles?: number;
  data_quality_score?: number;
  services?: string[];
  languages_spoken?: string[];
  accepts_medicaid?: boolean;
  accepts_medicare?: boolean;
  hours_of_operation?: any;
  wheelchair_accessible?: boolean;
  status?: string;
}

interface DirectoryProps {
  onNavigateToCapacity?: () => void;
  onBack?: () => void;
  onProviderSelected?: (provider: Provider) => void;
  onNavigateToCommunications?: (provider: Provider) => void;
}

export function Directory({ onNavigateToCapacity, onBack, onProviderSelected, onNavigateToCommunications }: DirectoryProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [myProviders, setMyProviders] = useState<Provider[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSavedProviders();
  }, []);

  const fetchSavedProviders = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch(`${config.api.baseUrl}/api/saved-providers`);

      if (!response.ok) {
        throw new Error(`Failed to fetch saved providers: ${response.status}`);
      }

      const data = await response.json();
      let providers = Array.isArray(data) ? data : (data.providers || []);

      providers = providers.map((provider: any) => {
        let parsedServices: string[] = [];
        if (Array.isArray(provider.services)) {
          parsedServices = provider.services;
        } else if (typeof provider.services === 'string' && provider.services.length > 0) {
          parsedServices = provider.services.split(',').map((s: string) => s.trim());
        }

        let parsedLanguages: string[] = [];
        if (Array.isArray(provider.languages_spoken)) {
          parsedLanguages = provider.languages_spoken;
        } else if (typeof provider.languages_spoken === 'string' && provider.languages_spoken.length > 0) {
          parsedLanguages = provider.languages_spoken.split(',').map((s: string) => s.trim());
        }

        return { ...provider, services: parsedServices, languages_spoken: parsedLanguages } as Provider;
      });

      setMyProviders(providers);
    } catch (error) {
      console.error('Error loading saved providers:', error);
      setMyProviders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredProviders = myProviders.filter(provider => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      provider.organization_name?.toLowerCase().includes(query) ||
      provider.city?.toLowerCase().includes(query) ||
      provider.state?.toLowerCase().includes(query) ||
      provider.organization_type?.toLowerCase().includes(query) ||
      provider.services?.some(service => service.toLowerCase().includes(query)) ||
      provider.street_address?.toLowerCase().includes(query)
    );
  });

  const handleGetDirections = (provider: Provider) => {
    if (provider.latitude && provider.longitude) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${provider.latitude},${provider.longitude}`, '_blank');
    } else {
      const address = `${provider.street_address}, ${provider.city}, ${provider.state} ${provider.zip_code}`;
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`, '_blank');
    }
  };

  const handleRemoveProvider = async (providerId: string) => {
    if (!confirm('Are you sure you want to remove this provider from your list?')) return;

    try {
      const response = await authFetch(`${config.api.baseUrl}/api/saved-providers/${providerId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchSavedProviders();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to remove provider: ${response.status}`);
      }
    } catch (error) {
      console.error('Error removing provider:', error);
      alert(`Failed to remove provider: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleAddProvider = async (provider: Provider) => {
    try {
      const response = await authFetch(`${config.api.baseUrl}/api/saved-providers`, {
        method: 'POST',
        jsonBody: provider
      });

      if (response.ok) {
        await fetchSavedProviders();
        setShowAddForm(false);
      } else if (response.status === 409) {
        alert('This provider is already saved');
        setShowAddForm(false);
      } else {
        throw new Error(`Failed to save provider: ${response.status}`);
      }
    } catch (error) {
      console.error('Error saving provider:', error);
      alert('Failed to save provider. Please try again.');
    }
  };

  if (showAddForm) {
    return (
      <AddProviderForm
        onSubmit={handleAddProvider}
        onCancel={() => setShowAddForm(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 sm:px-6 lg:px-8 py-4 md:py-6">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[13px] text-ink-500">
              {isLoading ? 'Loading...' : `${myProviders.length} saved provider${myProviders.length !== 1 ? 's' : ''}`}
            </p>
            <div className="flex-1" />
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-v2accent hover:bg-v2accent/90 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 self-start md:self-auto"
            >
              <Plus className="w-4 h-4" />
              Add Provider
            </Button>
          </div>

          {/* Search */}
          {myProviders.length > 0 && (
            <div className="mt-4 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, city, type, or services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-4 md:py-6 max-w-5xl mx-auto">
        {/* Loading */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading your providers...</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && myProviders.length === 0 && (
          <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md">
            <CardContent className="p-8 md:p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="w-8 h-8 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No Saved Providers</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                Search for providers in the Provider Directory and save them here for quick access to contact info, directions, and messaging.
              </p>
              <Button
                onClick={() => setShowAddForm(true)}
                className="bg-v2accent hover:bg-v2accent/90 text-white px-6 py-2 rounded-lg text-sm font-medium"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Provider Manually
              </Button>
            </CardContent>
          </Card>
        )}

        {/* No search results */}
        {!isLoading && myProviders.length > 0 && filteredProviders.length === 0 && (
          <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md">
            <CardContent className="p-8 text-center">
              <Search className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">No matches</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                No providers match "{searchQuery}". Try a different search term.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Provider Cards */}
        {!isLoading && filteredProviders.length > 0 && (
          <div className="space-y-3">
            {searchQuery && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing {filteredProviders.length} of {myProviders.length} providers
              </p>
            )}

            {filteredProviders.map((provider, index) => (
              <Card key={provider.id || `provider-${index}`} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4 sm:p-5">
                  {/* Provider Header */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {provider.organization_name || 'Unknown Provider'}
                      </h3>
                      {provider.organization_type && (
                        <span className="inline-block mt-1 px-2.5 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full">
                          {provider.organization_type}
                        </span>
                      )}
                    </div>
                    {provider.distance_miles != null && (
                      <div className="text-right flex-shrink-0">
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{provider.distance_miles.toFixed(1)}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">miles</div>
                      </div>
                    )}
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-1.5 mb-3 text-sm">
                    {provider.street_address && (
                      <div className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                        <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <span>{provider.street_address}, {provider.city}, {provider.state} {provider.zip_code}</span>
                      </div>
                    )}
                    {provider.primary_phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={`tel:${provider.primary_phone}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                          {provider.primary_phone}
                        </a>
                      </div>
                    )}
                    {provider.primary_email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={`mailto:${provider.primary_email}`} className="text-blue-600 dark:text-blue-400 hover:underline truncate">
                          {provider.primary_email}
                        </a>
                      </div>
                    )}
                    {provider.website && (
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={provider.website.startsWith('http') ? provider.website : `https://${provider.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline truncate">
                          Visit Website
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Services */}
                  {provider.services && provider.services.length > 0 && (
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-1.5">
                        {provider.services.slice(0, 5).map((service, i) => (
                          <span key={i} className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                            {service}
                          </span>
                        ))}
                        {provider.services.length > 5 && (
                          <span className="px-2 py-0.5 text-xs text-gray-500">+{provider.services.length - 5} more</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Feature badges */}
                  {(provider.accepts_medicaid || provider.accepts_medicare || provider.wheelchair_accessible || (provider.languages_spoken && provider.languages_spoken.length > 1)) && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {provider.accepts_medicaid && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-300">
                          <CheckCircle className="w-3.5 h-3.5" /> Medicaid
                        </span>
                      )}
                      {provider.accepts_medicare && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-300">
                          <CheckCircle className="w-3.5 h-3.5" /> Medicare
                        </span>
                      )}
                      {provider.wheelchair_accessible && (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-700 dark:text-blue-300">
                          <CheckCircle className="w-3.5 h-3.5" /> Accessible
                        </span>
                      )}
                      {provider.languages_spoken && provider.languages_spoken.length > 1 && (
                        <span className="inline-flex items-center gap-1 text-xs text-purple-700 dark:text-purple-300">
                          <Globe className="w-3.5 h-3.5" /> {provider.languages_spoken.length} Languages
                        </span>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleGetDirections(provider)}
                      icon={<Navigation className="w-4 h-4" />}
                    >
                      Directions
                    </Button>
                    {onNavigateToCommunications && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onNavigateToCommunications(provider)}
                        icon={<MessageCircle className="w-4 h-4" />}
                      >
                        Messages
                      </Button>
                    )}
                    {onProviderSelected && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onProviderSelected(provider)}
                        icon={<CheckCircle className="w-4 h-4" />}
                        className="text-green-600 dark:text-green-400 border-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                      >
                        Select
                      </Button>
                    )}
                    <div className="flex-1" />
                    {provider.id && (
                      <button
                        onClick={() => handleRemoveProvider(provider.provider_id!)}
                        className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 flex items-center gap-1 transition-colors"
                        title="Remove from My Providers"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Remove
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
